import { EXPECTED_STATUS } from '@dealstack/shared/lib/common/business/kyc';
import {
	AWAITING_CERTIFIER_STATUS,
	KycFileDisplayStatus,
	REQUESTED_STATUS,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/statusStyling.helper';
import { ORDERED_KYC_STATUSES } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/investment.types.ts';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { AcceptanceStatus, KYCWorkflowStatus } from '@dealstack/shared/lib/generated';
import { GetKYCWorkflowRowRes } from '@dealstack/shared/lib/generated/models/GetKYCWorkflowRowRes';
import { KYCWorkflowFileItemRes } from '@dealstack/shared/lib/generated/models/KYCWorkflowFileItemRes';
import { QuickFilterOption } from '@dealstack/frontend/shared/components/Table/MantineTable/QuickFiltersSelect/QuickFiltersSelect.types';

export { REQUESTED_STATUS };
export type KYCDocumentStatus = AcceptanceStatus | typeof REQUESTED_STATUS;

export type KYCWorkflowsFilter = {
	investor?: string;
	investorId?: string | string[];
	status?: string[];
	investmentType?: string[];
	declarationsStatus?: string[];
	proofOfIdentityStatus?: string[];
	proofOfAddressStatus?: string[];
	investmentVehicleStatus?: string[];
	formsStatus?: string[];
	cohorts?: string[];
};

export const KYC_STATUS_ORDER: KYCWorkflowStatus[] = [
	KYCWorkflowStatus.APPROVED,
	KYCWorkflowStatus.IN_PROGRESS,
	KYCWorkflowStatus.DEFINITION_COMPLETE,
	KYCWorkflowStatus.OPEN,
	KYCWorkflowStatus.WITHDRAWN,
];

export const getDocumentStatusLabel = (status: KYCDocumentStatus): string => {
	switch (status) {
		case AcceptanceStatus.APPROVED:
			return 'Approved';
		case AcceptanceStatus.REJECTED:
			return 'Rejected';
		case AcceptanceStatus.REREQUESTED:
			return 'Rerequested';
		case AcceptanceStatus.PENDING:
			return 'Ready for Review';
		case REQUESTED_STATUS:
			return 'Requested';
	}
};

export const getKycStatusDotColor = (status: KYCWorkflowStatus): string => {
	switch (status) {
		case KYCWorkflowStatus.APPROVED:
			return defaultTheme.v3.colors.teal[8];
		case KYCWorkflowStatus.IN_PROGRESS:
			return defaultTheme.v3.colors.orange[6];
		case KYCWorkflowStatus.DEFINITION_COMPLETE:
			return defaultTheme.v3.colors.gray[9];
		case KYCWorkflowStatus.OPEN:
			return defaultTheme.v3.colors.gray[9];
		case KYCWorkflowStatus.WITHDRAWN:
			return defaultTheme.v3.colors.gray[9];
	}
};

export const getKycStatusLabel = (status: KYCWorkflowStatus): string => {
	switch (status) {
		case KYCWorkflowStatus.APPROVED:
			return 'Approved';
		case KYCWorkflowStatus.IN_PROGRESS:
			return 'In Progress';
		case KYCWorkflowStatus.DEFINITION_COMPLETE:
			return 'Definition Complete';
		case KYCWorkflowStatus.OPEN:
			return 'Open';
		case KYCWorkflowStatus.WITHDRAWN:
			return 'Withdrawn';
	}
};

export type DocumentStatusField = keyof Pick<
	GetKYCWorkflowRowRes,
	| 'declarationsStatus'
	| 'proofOfIdentityStatus'
	| 'proofOfAddressStatus'
	| 'investmentVehicleStatus'
	| 'formsStatus'
>;

export const FIELD_TO_ITEMS_KEY: Record<DocumentStatusField, keyof GetKYCWorkflowRowRes> = {
	declarationsStatus: 'declarationItems',
	proofOfIdentityStatus: 'poiFiles',
	proofOfAddressStatus: 'poaFiles',
	investmentVehicleStatus: 'vehicleFiles',
	formsStatus: 'formFiles',
};

export const getKycStatusCount = (
	workflows: GetKYCWorkflowRowRes[],
	status: KYCWorkflowStatus
): number => workflows.filter((w) => w.status === status).length;

export const getUniqueInvestorCount = (workflows: GetKYCWorkflowRowRes[]): number =>
	new Set(workflows.map((w) => w.investorId)).size;

type FileDisplayFields = Pick<
	KYCWorkflowFileItemRes,
	'status' | 'isInherited' | 'isAwaitingCertifier'
>;

export const getFileDisplayStatus = (file: FileDisplayFields): KycFileDisplayStatus => {
	if (file.isInherited) {
		return EXPECTED_STATUS;
	}
	if (file.isAwaitingCertifier) {
		return AWAITING_CERTIFIER_STATUS;
	}
	return file.status ?? EXPECTED_STATUS;
};

/** The standard statuses always show; "Awaiting certifier" only when something is in it. */
export const visibleKycFileStatuses = (
	countFor: (status: KycFileDisplayStatus) => number
): KycFileDisplayStatus[] =>
	countFor(AWAITING_CERTIFIER_STATUS) > 0
		? ORDERED_KYC_STATUSES.flatMap((status) =>
				status === AcceptanceStatus.PENDING ? [AWAITING_CERTIFIER_STATUS, status] : [status]
			)
		: ORDERED_KYC_STATUSES;

export const groupFilesByDisplayStatus = (
	files: KYCWorkflowFileItemRes[]
): { status: KycFileDisplayStatus; items: { text: string }[] }[] => {
	const itemsFor = (status: KycFileDisplayStatus) =>
		files.filter((f) => getFileDisplayStatus(f) === status).map((f) => ({ text: f.fileName }));
	return visibleKycFileStatuses((status) => itemsFor(status).length).map((status) => ({
		status,
		items: itemsFor(status),
	}));
};

export const getDocumentItemCounts = (
	workflows: GetKYCWorkflowRowRes[],
	field: DocumentStatusField
): Record<KycFileDisplayStatus, number> => {
	const itemsKey = FIELD_TO_ITEMS_KEY[field];
	const counts: Record<KycFileDisplayStatus, number> = {
		[EXPECTED_STATUS]: 0,
		[AWAITING_CERTIFIER_STATUS]: 0,
		[AcceptanceStatus.PENDING]: 0,
		[AcceptanceStatus.APPROVED]: 0,
		[AcceptanceStatus.REJECTED]: 0,
		[AcceptanceStatus.REREQUESTED]: 0,
	};

	for (const w of workflows) {
		if (w[field] == null) {
			continue;
		}

		const items = (w[itemsKey] as FileDisplayFields[]) ?? [];
		if (!items.length) {
			counts[EXPECTED_STATUS]++;
			continue;
		}
		for (const item of items) {
			counts[getFileDisplayStatus(item)]++;
		}
	}

	return counts;
};

export const DOCUMENT_STATUS_FILTER_OPTIONS = (
	[
		AcceptanceStatus.APPROVED,
		AcceptanceStatus.PENDING,
		AcceptanceStatus.REJECTED,
		AcceptanceStatus.REREQUESTED,
		REQUESTED_STATUS,
	] satisfies KYCDocumentStatus[]
).map((s) => ({ value: s, label: getDocumentStatusLabel(s) }));

export const KYC_STATUS_FILTER_OPTIONS = KYC_STATUS_ORDER.filter(
	(s) => s !== KYCWorkflowStatus.OPEN
).map((s) => ({
	value: s,
	label: getKycStatusLabel(s),
}));

export enum InvestmentDisplayType {
	BUYING = 'BUYING',
	SELLING = 'SELLING',
	STANDALONE = 'STANDALONE',
}

export const INVESTMENT_DISPLAY_TYPE_ORDER: InvestmentDisplayType[] = [
	InvestmentDisplayType.BUYING,
	InvestmentDisplayType.SELLING,
	InvestmentDisplayType.STANDALONE,
];

export const getInvestmentDisplayType = (investmentType: string | null): InvestmentDisplayType => {
	if (!investmentType) {
		return InvestmentDisplayType.STANDALONE;
	}
	if (['JOINER', 'JOINER_CREATE', 'ALLOCATION'].includes(investmentType)) {
		return InvestmentDisplayType.BUYING;
	}
	if (investmentType === 'LEAVER') {
		return InvestmentDisplayType.SELLING;
	}
	return InvestmentDisplayType.STANDALONE;
};

export const getInvestmentDisplayTypeLabel = (type: InvestmentDisplayType): string => {
	switch (type) {
		case InvestmentDisplayType.BUYING:
			return 'Buying';
		case InvestmentDisplayType.SELLING:
			return 'Selling';
		case InvestmentDisplayType.STANDALONE:
			return 'Standalone';
	}
};

export const getInvestmentDisplayTypeColor = (type: InvestmentDisplayType): string => {
	switch (type) {
		case InvestmentDisplayType.BUYING:
			return defaultTheme.v3.colors.blue[6];
		case InvestmentDisplayType.SELLING:
			return defaultTheme.v3.colors.orange[6];
		case InvestmentDisplayType.STANDALONE:
			return defaultTheme.v3.colors.gray[6];
	}
};

export const getInvestmentDisplayTypeBadgeColor = (type: InvestmentDisplayType): string => {
	switch (type) {
		case InvestmentDisplayType.BUYING:
			return 'blue';
		case InvestmentDisplayType.SELLING:
			return 'orange';
		case InvestmentDisplayType.STANDALONE:
			return 'dark.3';
	}
};

export const getInvestmentTypeCount = (
	workflows: GetKYCWorkflowRowRes[],
	type: InvestmentDisplayType
): number => workflows.filter((w) => getInvestmentDisplayType(w.investmentType) === type).length;

export const INVESTMENT_TYPE_FILTER_OPTIONS = INVESTMENT_DISPLAY_TYPE_ORDER.map((t) => ({
	value: t,
	label: getInvestmentDisplayTypeLabel(t),
}));

export const createQuickFilterOptions = (t: (key: string) => string): QuickFilterOption[] => [
	{
		value: 'in_progress',
		label: t('kyc.manage.filter.in_progress'),
		filterKey: 'status',
		filterValue: KYCWorkflowStatus.IN_PROGRESS,
	},
	{
		value: 'completed',
		label: t('kyc.manage.filter.completed'),
		filterKey: 'status',
		filterValue: KYCWorkflowStatus.APPROVED,
	},
	{
		value: 'all',
		label: t('kyc.manage.filter.all'),
	},
];
